import React from 'react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Tooltip, Legend } from 'recharts';

interface CategoryPerformanceChartProps {
  data: Array<{
    category: string;
    average_progress: number;
    workshop_count: number;
    completion_rate: number;
  }>;
  loading?: boolean;
}

const CategoryPerformanceChart: React.FC<CategoryPerformanceChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Performance</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Performance</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No category data available
        </div>
      </div>
    );
  }

  const formatCategory = (category: string) => {
    return category.toUpperCase();
  };

  const getCategoryColor = (category: string) => {
    const colors = {
      'Safety': '#ef4444',      // Red
      'Quality': '#3b82f6',    // Blue
      'Delivery': '#10b981',   // Green
      'Cost': '#f59e0b',       // Yellow
      'Morale': '#8b5cf6'      // Purple
    };
    return colors[category as keyof typeof colors] || '#6b7280';
  };

  const maxValue = Math.max(...data.map(d => d.average_progress));

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Performance</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <PolarGrid />
            <PolarAngleAxis 
              dataKey="category" 
              tick={{ fontSize: 12 }}
              tickFormatter={formatCategory}
            />
            <PolarRadiusAxis 
              angle={90} 
              domain={[0, 100]} 
              tick={{ fontSize: 10 }}
            />
            <Radar
              name="Average Progress"
              dataKey="average_progress"
              stroke="#3b82f6"
              fill="#3b82f6"
              fillOpacity={0.3}
              strokeWidth={2}
            />
            <Radar
              name="Completion Rate"
              dataKey="completion_rate"
              stroke="#10b981"
              fill="#10b981"
              fillOpacity={0.2}
              strokeWidth={2}
            />
            <Tooltip 
              formatter={(value: number, name: string) => [`${value.toFixed(1)}%`, name]}
              labelFormatter={(label) => `Category: ${formatCategory(label)}`}
            />
            <Legend />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Category Breakdown</h4>
          <div className="space-y-2">
            {[...data]
              .sort((a, b) => b.average_progress - a.average_progress)
              .map((item) => (
                <div key={item.category} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded mr-2" 
                      style={{ backgroundColor: getCategoryColor(item.category) }}
                    ></div>
                    <span className="text-sm font-medium">{formatCategory(item.category)}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{item.workshop_count}</span> workshops
                    <span className="ml-2 text-xs px-2 py-1 bg-blue-100 text-blue-600 rounded">
                      {item.average_progress.toFixed(1)}%
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Performance Insights</h4>
          <div className="space-y-3">
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Best Category</span>
                <span className="font-bold text-green-600">
                  {data.reduce((best, item) => item.average_progress > best.average_progress ? item : best, data[0])?.category}
                </span>
              </div>
              <div className="text-xs text-green-500 mt-1">
                {Math.max(...data.map(d => d.average_progress)).toFixed(1)}% average progress
              </div>
            </div>
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-600">Most Active</span>
                <span className="font-bold text-blue-600">
                  {data.reduce((max, item) => item.workshop_count > max.workshop_count ? item : max, data[0])?.category}
                </span>
              </div>
              <div className="text-xs text-blue-500 mt-1">
                {Math.max(...data.map(d => d.workshop_count))} workshops
              </div>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Average Performance</span>
                <span className="font-bold text-gray-900">
                  {(data.reduce((sum, item) => sum + item.average_progress, 0) / data.length).toFixed(1)}%
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                Across all categories
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryPerformanceChart;
